import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import styles from './Kanban.css';

export const Kanban = ({
    items = [],
    statusKey = 'status',
    columns = {},
    onDragStart = () => { },
    onDragEnd = () => { },
    onDragCancel = () => { },
    renderCard,
    disabled = false,
    draggable = true,
    completionZones = null,
    resetDraggingTrigger = null,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const scrollContainerRef = useRef(null);
    const scrollIntervalRef = useRef(null);

    useEffect(() => {
        if (resetDraggingTrigger !== null) {
            setIsDragging(false);
        }
    }, [resetDraggingTrigger]);

    // Автоскролл при перетаскивании
    useEffect(() => {
        if (!isDragging) {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;
            }
            return;
        }

        const handleMouseMove = (e) => {
            if (!scrollContainerRef.current) return;

            const container = scrollContainerRef.current;
            const rect = container.getBoundingClientRect();
            const scrollZone = 100;
            const scrollSpeed = 15;

            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;
            }

            const mouseX = e.clientX;

            if (mouseX < rect.left + scrollZone) {
                scrollIntervalRef.current = setInterval(() => {
                    if (container.scrollLeft > 0) {
                        container.scrollLeft -= scrollSpeed;
                    }
                }, 20);
            }
            else if (mouseX > rect.right - scrollZone) {
                scrollIntervalRef.current = setInterval(() => {
                    const maxScroll = container.scrollWidth - container.clientWidth;
                    if (container.scrollLeft < maxScroll) {
                        container.scrollLeft += scrollSpeed;
                    }
                }, 20);
            }
        };

        const handleMouseUp = () => {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;
            }
        };
    }, [isDragging]);

    const handleDragStart = (result) => {
        if (disabled || !draggable) return;

        setIsDragging(true);
        const { source } = result;
        const sourceItems = items.filter(item => item[statusKey] === source.droppableId);
        const draggedItem = sourceItems[source.index];

        if (draggedItem) {
            onDragStart(draggedItem);
        }
    };

    const handleDragEnd = (result) => {
        if (disabled || !draggable) return;

        const { source, destination } = result;
        if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
            setIsDragging(false);
            const sourceItems = items.filter(item => item[statusKey] === source.droppableId);
            const draggedItem = sourceItems[source.index];
            if (draggedItem) {
                onDragCancel(draggedItem);
            }
            return;
        }

        if (completionZones && (
            destination.droppableId === 'completion-success' ||
            destination.droppableId === 'completion-failure' ||
            destination.droppableId === 'completion-reserve'
        )) {
            const sourceItems = items.filter(item => item[statusKey] === source.droppableId);
            const movedItem = sourceItems[source.index];

            if (movedItem) {
                onDragEnd(movedItem, destination.droppableId, destination.index);
            }
            return;
        }

        const targetColumn = columns[destination.droppableId];
        if (targetColumn && targetColumn.disabled) {
            setIsDragging(false);
            return;
        }

        const itemsCopy = [...items];
        const sourceItems = itemsCopy.filter(item => item[statusKey] === source.droppableId);
        const movedItem = sourceItems[source.index];

        if (!movedItem) {
            setIsDragging(false);
            return;
        }

        const updatedItems = itemsCopy.filter(item => {
            if (item[statusKey] === source.droppableId) {
                return !(item.id === movedItem.id);
            }
            return true;
        });

        const destinationItems = updatedItems.filter(item => item[statusKey] === destination.droppableId);
        let insertIndex = 0;

        if (destinationItems.length > 0) {
            if (destination.index === 0) {
                insertIndex = updatedItems.findIndex(item => item[statusKey] === destination.droppableId);
            } else if (destination.index >= destinationItems.length) {
                const lastDestItem = destinationItems[destinationItems.length - 1];
                insertIndex = updatedItems.findIndex(item => item.id === lastDestItem.id) + 1;
            } else {
                const itemAfter = destinationItems[destination.index];
                insertIndex = updatedItems.findIndex(item => item.id === itemAfter.id);
            }
        } else {
            const columnKeys = Object.keys(columns);
            const destIndex = columnKeys.indexOf(destination.droppableId);

            if (destIndex > 0) {
                const prevColumnKey = columnKeys[destIndex - 1];
                const prevColumnItems = updatedItems.filter(item => item[statusKey] === prevColumnKey);
                if (prevColumnItems.length > 0) {
                    const lastItem = prevColumnItems[prevColumnItems.length - 1];
                    insertIndex = updatedItems.findIndex(item => item.id === lastItem.id) + 1;
                }
            }
        }

        const updatedItem = { ...movedItem, [statusKey]: destination.droppableId };
        updatedItems.splice(insertIndex, 0, updatedItem);

        onDragEnd(updatedItem, destination.droppableId, destination.index);
        setIsDragging(false);
    };

    const groupedItems = items.reduce((acc, item) => {
        const key = item[statusKey] || 'unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});

    // Глобальные стили для портала перетаскивания
    useEffect(() => {
        const styleId = 'kanban-drag-styles';

        if (document.getElementById(styleId)) {
            return;
        }

        const style = document.createElement('style');
        style.id = styleId;
        style.innerHTML = `
            /* Исправленные стили для перетаскивания */
            [data-rbd-drag-handle-context-id] {
                position: relative !important;
            }
            
            /* Портал для перетаскиваемых элементов */
            [data-rbd-draggable-id][style*="position: fixed"],
            [data-rbd-draggable-id][style*="position:fixed"] {
                z-index: 999999 !important;
                position: fixed !important;
            }
            
            /* Все элементы портала */
            body > [data-rbd-droppable-id] {
                z-index: 999999 !important;
                position: fixed !important;
            }
            
            /* Перетаскиваемый элемент */
            [data-rbd-drag-handle-draggable-id] {
                z-index: 999999 !important;
            }

            /* Контекст перетаскивания */
            [data-rbd-draggable-context-id] {
                z-index: 999999 !important;
            }

            /* Элемент во время перетаскивания */
            .react-beautiful-dnd-draggable {
                z-index: 999999 !important;
            }

            /* Убираем transform для родителей чтобы не создавали stacking context */
            .kanban [data-rbd-droppable-id] {
                transform: none !important;
                position: static !important;
            }

            /* Исправляем позиционирование колонок */
            .kanban .column {
                position: relative !important;
                z-index: 1 !important;
            }

            /* Исправляем позиционирование карточек */
            .kanban [data-rbd-draggable-id]:not([style*="position: fixed"]) {
                position: relative !important;
                z-index: auto !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            const existingStyle = document.getElementById(styleId);
            if (existingStyle && document.head.contains(existingStyle)) {
                document.head.removeChild(existingStyle);
            }
        };
    }, []);

    return (
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div style={{
                width: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
            }}>
                <div
                    ref={scrollContainerRef}
                    style={{
                        width: '100%',
                        overflowX: 'auto',
                        flex: 1,
                        position: 'relative',
                    }}
                >
                    <div className="kanban" style={{
                        display: 'flex',
                        minWidth: 'fit-content',
                        paddingBottom: '12px',
                        gap: '8px',
                        position: 'relative',
                        height: '100%'
                    }}>
                        {Object.entries(columns).map(([statusKey, { title, color, disabled }]) => (
                            <div
                                className="column"
                                key={statusKey}
                                style={{
                                    minWidth: '280px',
                                    width: '280px',
                                    flex: '0 0 280px',
                                    opacity: 1,
                                    pointerEvents: disabled ? 'none' : 'auto',
                                    position: 'relative',
                                    filter: disabled ? 'grayscale(50%)' : 'none'
                                }}
                            >
                                <div className="columnHeader" style={{
                                    borderTop: `4px solid ${color}`,
                                    backgroundColor: disabled ? '#f5f5f5' : 'white',
                                    padding: '12px 8px',
                                    minHeight: '60px'
                                }}>
                                    <h3 style={{
                                        color: disabled ? '#999' : 'inherit',
                                        fontSize: '14px',
                                        margin: '0 0 4px 0',
                                        lineHeight: '1.2'
                                    }}>{title}</h3>
                                    <span className="countBadge" style={{
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}>{(groupedItems[statusKey] || []).length}</span>
                                    {disabled && (
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#999',
                                            marginTop: '4px',
                                            fontStyle: 'italic'
                                        }}>
                                            Недоступно
                                        </div>
                                    )}
                                </div>
                                <Droppable droppableId={statusKey} isDropDisabled={disabled}>
                                    {(provided, snapshot) => (
                                        <div
                                            className="cards"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            data-is-dragging-over={snapshot.isDraggingOver}
                                            style={{
                                                minHeight: '80px',
                                                maxHeight: 'calc(100vh - 300px)',
                                                overflowY: 'auto',
                                                backgroundColor: snapshot.isDraggingOver ? '#e3f2fd' : (disabled ? '#f9f9f9' : 'transparent'),
                                                padding: '8px',
                                                transition: 'background-color 0.2s ease, border 0.2s ease',
                                                border: snapshot.isDraggingOver ? '2px dashed #2196f3' : '2px dashed transparent',
                                                position: 'relative',
                                                zIndex: 1
                                            }}
                                        >
                                            {(groupedItems[statusKey] || []).map((item, index) => (
                                                <Draggable
                                                    draggableId={`${item.id}`}
                                                    index={index}
                                                    key={item.id}
                                                    isDragDisabled={disabled || !draggable}
                                                >
                                                    {(provided, snapshot) => {
                                                        const draggableStyle = provided.draggableProps.style;
                                                        const style = {
                                                            ...draggableStyle,
                                                            marginBottom: '6px',
                                                            opacity: disabled ? 0.7 : 1,
                                                            cursor: (disabled || !draggable) ? 'default' : snapshot.isDragging ? 'grabbing' : 'grab',
                                                        };

                                                        // Убираем кастомные стили при перетаскивании - библиотека сама управляет позиционированием
                                                        if (snapshot.isDragging) {
                                                            style.opacity = 0.9;
                                                            style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.25)';
                                                            style.border = '2px solid #2196f3';
                                                            style.borderRadius = '8px';
                                                        }

                                                        return (
                                                            <div
                                                                className={styles.cardWrapper}
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...(disabled || !draggable ? {} : provided.dragHandleProps)}
                                                                style={style}
                                                                data-is-dragging={snapshot.isDragging}
                                                            >
                                                                {renderCard(item)}
                                                            </div>
                                                        );
                                                    }}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Зоны завершения */}
                {completionZones && (
                    <div style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 250,
                        right: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        borderTop: '2px solid #d9d9d9',
                        padding: '16px',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '16px',
                        zIndex: 1000,
                        backdropFilter: 'blur(4px)',
                        opacity: isDragging ? 1 : 0,
                        pointerEvents: isDragging ? 'auto' : 'none',
                        transition: 'opacity 0.2s ease',
                        visibility: isDragging ? 'visible' : 'hidden'
                    }}>
                        {/* Успех */}
                        <Droppable droppableId="completion-success">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{
                                        minWidth: '300px',
                                        maxWidth: '300px',
                                        height: '100px',
                                        minHeight: '100px',
                                        maxHeight: '100px',
                                        padding: '16px',
                                        backgroundColor: snapshot.isDraggingOver ? '#52c41a' : '#f6ffed',
                                        border: `2px dashed ${snapshot.isDraggingOver ? '#52c41a' : '#b7eb8f'}`,
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        color: snapshot.isDraggingOver ? '#fff' : '#52c41a',
                                        marginBottom: '4px'
                                    }}>
                                        ✓ Успех
                                    </div>
                                    <div style={{ display: 'none' }}>
                                        {provided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>

                        {/* Провал */}
                        <Droppable droppableId="completion-failure">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{
                                        minWidth: '300px',
                                        maxWidth: '300px',
                                        height: '100px',
                                        minHeight: '100px',
                                        maxHeight: '100px',
                                        padding: '16px',
                                        backgroundColor: snapshot.isDraggingOver ? '#ff4d4f' : '#fff1f0',
                                        border: `2px dashed ${snapshot.isDraggingOver ? '#ff4d4f' : '#ffccc7'}`,
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        color: snapshot.isDraggingOver ? '#fff' : '#ff4d4f',
                                        marginBottom: '4px'
                                    }}>
                                        ✗ Провал
                                    </div>
                                    <div style={{ display: 'none' }}>
                                        {provided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>

                        {/* Кандидат в резерве */}
                        <Droppable droppableId="completion-reserve">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{
                                        minWidth: '300px',
                                        maxWidth: '300px',
                                        height: '100px',
                                        minHeight: '100px',
                                        maxHeight: '100px',
                                        padding: '16px',
                                        backgroundColor: snapshot.isDraggingOver ? '#1890ff' : '#e6f7ff',
                                        border: `2px dashed ${snapshot.isDraggingOver ? '#1890ff' : '#91d5ff'}`,
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}
                                >
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        color: snapshot.isDraggingOver ? '#fff' : '#1890ff',
                                        marginBottom: '4px'
                                    }}>
                                        Кандидат в резерве
                                    </div>
                                    <div style={{ display: 'none' }}>
                                        {provided.placeholder}
                                    </div>
                                </div>
                            )}
                        </Droppable>
                    </div>
                )}
            </div>
        </DragDropContext>
    );
};